import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, Download, Filter } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, DatePicker, Table } from '../../components/common';
import { reportApi } from '../../services/api';
import './Reports.css';

export default function InvoiceAgingReport() {
    const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['reports', 'invoice-aging', asOfDate],
        queryFn: () => reportApi.getInvoiceAging({ asOfDate }),
        select: (res) => res.data?.data || { buckets: {}, details: [] }
    });

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency', currency: 'IDR', minimumFractionDigits: 0
        }).format(value || 0);
    };

    const columns = [
        { key: 'invoiceNumber', title: 'Invoice', dataIndex: 'invoiceNumber' },
        { key: 'vendor', title: 'Vendor', render: (_, row) => row.vendor?.name },
        { key: 'amount', title: 'Amount', dataIndex: 'amount', align: 'right', render: (val) => formatCurrency(val) },
        {
            key: 'dueDate', title: 'Due Date', dataIndex: 'dueDate',
            render: (date) => new Date(date).toLocaleDateString()
        },
        {
            key: 'daysOverdue', title: 'Days Overdue', dataIndex: 'daysOverdue', align: 'center',
            render: (days) => <span className={days > 0 ? 'text-danger' : ''}>{days}</span>
        }
    ];

    const buckets = data?.buckets || {};

    return (
        <div className="report-page">
            <PageHeader
                title="Invoice Aging Report"
                subtitle="Track overdue invoices"
                breadcrumbs={[
                    { label: 'Reports', path: '/reports' },
                    { label: 'Invoice Aging' }
                ]}
                actions={<Button icon={Download}>Export</Button>}
            />

            <Card>
                <CardBody>
                    <div className="report-filters">
                        <DatePicker label="As of Date" value={asOfDate} onChange={setAsOfDate} />
                        <Button icon={Filter} onClick={() => refetch()}>Apply</Button>
                    </div>
                </CardBody>
            </Card>

            <Card>
                <CardBody>
                    <div className="aging-buckets">
                        <div className="aging-bucket current">
                            <h5>Current</h5>
                            <div className="amount">{formatCurrency(buckets.current?.amount)}</div>
                            <div className="count">{buckets.current?.count || 0} invoices</div>
                        </div>
                        <div className="aging-bucket days-30">
                            <h5>1-30 Days</h5>
                            <div className="amount">{formatCurrency(buckets.days30?.amount)}</div>
                            <div className="count">{buckets.days30?.count || 0} invoices</div>
                        </div>
                        <div className="aging-bucket days-60">
                            <h5>31-60 Days</h5>
                            <div className="amount">{formatCurrency(buckets.days60?.amount)}</div>
                            <div className="count">{buckets.days60?.count || 0} invoices</div>
                        </div>
                        <div className="aging-bucket days-90">
                            <h5>61-90 Days</h5>
                            <div className="amount">{formatCurrency(buckets.days90?.amount)}</div>
                            <div className="count">{buckets.days90?.count || 0} invoices</div>
                        </div>
                        <div className="aging-bucket days-over">
                            <h5>Over 90 Days</h5>
                            <div className="amount">{formatCurrency(buckets.over90?.amount)}</div>
                            <div className="count">{buckets.over90?.count || 0} invoices</div>
                        </div>
                    </div>
                </CardBody>
            </Card>

            <Card>
                <Table columns={columns} data={data?.details || []} loading={isLoading} rowKey="id" />
            </Card>
        </div>
    );
}
